import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";
import {
  ShieldCheck, Download, Copy, ArrowLeft, KeyRound, BadgeCheck, AlertTriangle,
  Chrome, Monitor, Apple, Terminal, FileText, CheckCircle2, XCircle,
} from "lucide-react";

const platformIcons = {
  windows: Monitor,
  macos: Apple,
  linux: Terminal,
  adobe_reader: FileText,
};

const platformLabels = {
  windows: "Microsoft Windows",
  macos: "Apple macOS",
  linux: "Linux (Debian/Ubuntu)",
  adobe_reader: "Adobe Acrobat Reader",
};

export const CertificatePortal = () => {
  const [tab, setTab] = useState("root");
  const [fingerprintInput, setFingerprintInput] = useState("");

  const manifest = useQuery({
    queryKey: ["cert-portal-manifest"],
    queryFn: async () => (await api.get("/cadastre360/security/certificates/portal-manifest")).data,
    retry: false,
  });

  const verify = useMutation({
    mutationFn: async (fp) => (await api.get("/cadastre360/security/certificates/verify-fingerprint", { params: { fingerprint: fp } })).data,
    onSuccess: (d) => {
      if (d.match) toast.success("Fingerprint match confirmed");
      else toast.error("Fingerprint does NOT match the Ministry certificate chain");
    },
    onError: () => toast.error("Verification failed"),
  });

  const doDownload = (kind) => {
    const url = API + "/cadastre360/security/certificates/" + kind + ".crt";
    const a = document.createElement("a");
    a.href = url;
    a.download = "cadastre360_ministry_" + kind + ".crt";
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Certificate downloaded");
  };

  const copyFingerprint = (fp) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(fp);
      toast.success("Fingerprint copied");
    }
  };

  const m = manifest.data;
  const currentCert = tab === "root" ? m?.root : m?.signer;

  return (
    <div className="min-h-screen p-6 lg:p-12" data-testid="page-cert-portal">
      <div className="flex items-center justify-between mb-8">
        <BrandMark size={140} />
        <Link to="/portal" className="cad-btn-outline px-3 py-1.5 rounded-sm text-[12px] inline-flex items-center gap-1.5">
          <ArrowLeft size={12} /> Public Portal
        </Link>
      </div>

      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">
            Cadastre360 · Ministry Trust Services
          </div>
          <h1 className="font-display text-4xl font-bold text-[#F6F8FA] mt-1">
            Ministry Root Certificate Portal
          </h1>
          <div className="text-[13px] text-[#B3C2CC] mt-2 max-w-3xl leading-relaxed">
            Download the official Cadastre360 Ministry Root Certificate. Installing it in your operating system or Adobe Acrobat trust
            store enables green-badge verification of every signed evidence pack, compliance document, and certificate issued by the
            Ministry of Mines and Mining Development, Republic of Zimbabwe.
          </div>
        </div>

        {manifest.isLoading && <div className="cad-panel p-8 text-[#B3C2CC] text-[13px]">Loading portal…</div>}
        {manifest.isError && (
          <div className="cad-panel p-8">
            <AlertTriangle className="text-[#E67E7E]" />
            <div className="text-[#F6F8FA] mt-2">The certificate portal is currently unavailable.</div>
          </div>
        )}

        {m && !m.available && (
          <div className="cad-panel p-8" data-testid="cert-portal-unavailable">
            <AlertTriangle className="text-[#E67E7E]" />
            <div className="text-[#F6F8FA] mt-2">Ministry certificate chain not yet provisioned.</div>
          </div>
        )}

        {m && m.available && (
          <>
            <div className="cad-panel px-2 py-2 flex flex-wrap gap-1 mb-4" data-testid="cert-portal-tabs">
              <button onClick={() => setTab("root")} data-testid="cert-tab-root"
                      className={"px-3 py-2 rounded-sm text-[11.5px] font-mono-cad uppercase tracking-[0.14em] inline-flex items-center gap-1.5 " +
                                 (tab === "root" ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")}>
                <ShieldCheck size={12} className="text-[#C99A2E]" /> Ministry Root CA
              </button>
              <button onClick={() => setTab("signer")} data-testid="cert-tab-signer"
                      className={"px-3 py-2 rounded-sm text-[11.5px] font-mono-cad uppercase tracking-[0.14em] inline-flex items-center gap-1.5 " +
                                 (tab === "signer" ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")}>
                <KeyRound size={12} className="text-[#C99A2E]" /> Document Signing Cert
              </button>
              <button onClick={() => setTab("verify")} data-testid="cert-tab-verify"
                      className={"px-3 py-2 rounded-sm text-[11.5px] font-mono-cad uppercase tracking-[0.14em] inline-flex items-center gap-1.5 " +
                                 (tab === "verify" ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")}>
                <BadgeCheck size={12} className="text-[#C99A2E]" /> Fingerprint Verification
              </button>
              <button onClick={() => setTab("install")} data-testid="cert-tab-install"
                      className={"px-3 py-2 rounded-sm text-[11.5px] font-mono-cad uppercase tracking-[0.14em] inline-flex items-center gap-1.5 " +
                                 (tab === "install" ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")}>
                <Download size={12} className="text-[#C99A2E]" /> Installation Guides
              </button>
            </div>

            {(tab === "root" || tab === "signer") && (
              <div className="cad-panel p-6" data-testid={"cert-detail-" + tab}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">
                      {tab === "root" ? "Ministry Root Certificate Authority" : "Document Signing Certificate"}
                    </div>
                    <div className="font-display text-xl font-bold text-[#F6F8FA] mt-1 break-words" data-testid="cert-common-name">
                      {currentCert.common_name}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-[12px]">
                      <div>
                        <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">Valid from</div>
                        <div className="text-[#F6F8FA] font-mono-cad" data-testid="cert-not-before">{currentCert.not_before}</div>
                      </div>
                      <div>
                        <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">Valid until</div>
                        <div className="text-[#F6F8FA] font-mono-cad" data-testid="cert-not-after">{currentCert.not_after}</div>
                      </div>
                      <div>
                        <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">Days remaining</div>
                        <div className="text-[#C99A2E] font-mono-cad" data-testid="cert-days">{currentCert.days_remaining}</div>
                      </div>
                      <div>
                        <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">Serial</div>
                        <div className="text-[#B3C2CC] font-mono-cad text-[10px] break-all">{currentCert.serial || "—"}</div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5] flex items-center gap-2">
                        SHA-256 Fingerprint
                        <button onClick={() => copyFingerprint(currentCert.sha256_fingerprint)}
                                data-testid="cert-copy-fp"
                                className="cad-btn-outline px-2 py-0.5 text-[9px] rounded-sm inline-flex items-center gap-1">
                          <Copy size={9} /> Copy
                        </button>
                      </div>
                      <div className="text-[11px] text-[#F6F8FA] font-mono-cad break-all mt-1" data-testid="cert-fingerprint">
                        {currentCert.sha256_fingerprint}
                      </div>
                    </div>
                  </div>
                  <div>
                    <button onClick={() => doDownload(tab)} data-testid={"cert-download-" + tab}
                            className="cad-btn-primary px-4 py-2 rounded-sm text-[12px] inline-flex items-center gap-2">
                      <Download size={13} /> Download {tab === "root" ? "root.crt" : "signing.crt"}
                    </button>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="cad-panel p-3">
                    <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5] flex items-center gap-1.5">
                      <Chrome size={11} /> Browser compatibility
                    </div>
                    <div className="text-[12px] text-[#F6F8FA] mt-1">{(m.browser_compatibility || []).join(" · ")}</div>
                  </div>
                  <div className="cad-panel p-3">
                    <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5] flex items-center gap-1.5">
                      <FileText size={11} /> Adobe compatibility
                    </div>
                    <div className="text-[12px] text-[#F6F8FA] mt-1">{(m.adobe_compatibility || []).join(" · ")}</div>
                  </div>
                </div>
              </div>
            )}

            {tab === "verify" && (
              <div className="cad-panel p-6" data-testid="cert-verify-panel">
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">
                  Independent Fingerprint Verification
                </div>
                <div className="text-[13px] text-[#B3C2CC] mt-2 max-w-2xl leading-relaxed">
                  Paste the SHA-256 fingerprint of the certificate you downloaded and Cadastre360 will confirm whether it matches
                  the currently active Ministry Root or Signing certificate. If the answer is "no match", do not install the file —
                  it is not from the Ministry.
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <input value={fingerprintInput} onChange={(e) => setFingerprintInput(e.target.value)}
                         placeholder="e.g. bbadaab5fd394da0…"
                         data-testid="cert-verify-input"
                         className="bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono-cad w-full md:w-[500px]" />
                  <button onClick={() => verify.mutate(fingerprintInput.trim())} disabled={!fingerprintInput.trim() || verify.isPending}
                          data-testid="cert-verify-btn"
                          className="cad-btn-primary px-4 py-2 rounded-sm text-[12px] inline-flex items-center gap-2">
                    <BadgeCheck size={12} /> Verify
                  </button>
                </div>
                {verify.data && (
                  <div className={"cad-panel p-3 mt-4 border-l-4 " + (verify.data.match ? "border-[#6BB88A]" : "border-[#E67E7E]")}
                       data-testid="cert-verify-result">
                    <div className="flex items-center gap-2">
                      {verify.data.match
                        ? <CheckCircle2 className="text-[#6BB88A]" size={16} />
                        : <XCircle className="text-[#E67E7E]" size={16} />}
                      <div className="text-[13px] text-[#F6F8FA] font-mono-cad">
                        {verify.data.match ? "MATCH — this fingerprint belongs to the Ministry." : "NO MATCH — do not install this file."}
                      </div>
                    </div>
                    <div className="mt-2 text-[11px] font-mono-cad text-[#B3C2CC]">
                      Matches root: {verify.data.matches_root ? "yes" : "no"} · Matches signer: {verify.data.matches_signer ? "yes" : "no"} ·
                      Verified at {verify.data.verified_at}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === "install" && (
              <div className="space-y-3" data-testid="cert-install-panel">
                {Object.entries(m.installation_guides || {}).map(([platform, steps]) => {
                  const Icon = platformIcons[platform] || Monitor;
                  return (
                    <div key={platform} className="cad-panel p-4" data-testid={"install-guide-" + platform}>
                      <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-2">
                        <Icon size={12} /> {platformLabels[platform] || platform}
                      </div>
                      <ol className="mt-2 space-y-1 list-decimal list-inside text-[12.5px] text-[#F6F8FA]">
                        {steps.map((s, i) => <li key={platform + "-" + i} className="leading-relaxed">{s}</li>)}
                      </ol>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div className="mt-6 text-[10px] font-mono-cad text-[#7C93A5] uppercase tracking-widest">
          Portal generated at {m?.portal_generated_at}
        </div>
      </div>
    </div>
  );
};
